import i18n from '../plugins/i18n';
import setHours from 'date-fns/set_hours';
import setMinutes from 'date-fns/set_minutes';
import { failureKeys, FPError, Lang } from 'types';
import pako from 'pako';

export function mapError(err: FPError | any): FPError {
  if (!err) {
    return {
      header: failureKeys.UNKNOWN,
      message: i18n.global.t('errors.UNKNOWN'),
      event_id: [],
    };
  }

  if (err.header === failureKeys.NETWORK_ERROR) {
    return {
      header: err.header,
      message: i18n.global.t('errors.NETWORK_ERROR'),
    };
  }

  return {
    header: err.header,
    message: err.message,
  };
}

export function clone<T>(instance: T): T {
  const copy = new (instance.constructor as { new (): T })();
  Object.assign(copy, instance);
  return copy;
}

export function hours24to12(hour: number) {
  const date = setMinutes(new Date(), 0);
  const new_date = setHours(date, hour);
  return new_date.toLocaleString(Lang.LANGS.US, {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatHoursDuration24to12(
  hours: string,
  divider: string = '-',
) {
  const splitted = hours.split(divider);
  if (
    splitted.length === 2 &&
    splitted[0].split(':')[0] &&
    splitted[1].split(':')[0] &&
    !splitted[0].split(':')[1].includes('AM') &&
    !splitted[0].split(':')[1].includes('PM')
  ) {
    return `${hours24to12(
      parseInt(splitted[0].split(':')[0]),
    )} ${divider} ${hours24to12(parseInt(splitted[1].split(':')[0]))}`;
  } else return hours;
}

export function parseQueryString(str) {
  let obj = {};
  let key;
  let value;
  (str || '').split('&').forEach(keyValue => {
    if (keyValue) {
      value = keyValue.split('=');
      key = decodeURIComponent(value[0]);
      obj[key] = !!value[1] ? decodeURIComponent(value[1]) : true;
    }
  });
  return obj;
}

export function readQuery(key: string) {
  const params = window.location.search
    .slice(1)
    .split('&')
    .map(p => p.split('='))
    .reduce((obj, pair) => {
      const [key, value] = pair.map(decodeURIComponent);
      obj[key] = value;
      return obj;
    }, {});

  return params[key];
}

export function getCleanedQuery(keys: string[]) {
  const params = window.location.search
    .slice(1)
    .split('&')
    .map(p => p.split('='))
    .reduce((obj, pair) => {
      const [key, value] = pair.map(decodeURIComponent);
      obj[key] = value;
      return obj;
    }, {});

  if (!keys.some(it => params[it])) {
    return params;
  }

  const toDelete = {};
  keys.forEach(it => {
    toDelete[it] = undefined;
  });

  const query = {
    ...params,
    ...toDelete,
  };

  return query;
}

export const formatJsonToQueryString = (json: any): string | null => {
  try {
    const jsonString = JSON.stringify(json);

    const compressed = pako.deflate(jsonString);

    const base64 = btoa(String.fromCharCode.apply(null, compressed));
    const urlSafe = base64
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');

    return urlSafe;
  } catch (e) {
    return null;
  }
};

export const formatQueryStringToJson = <T>(encoded: string): T | null => {
  try {
    const base64 = encoded.replace(/-/g, '+').replace(/_/g, '/');

    const binaryString = atob(base64);

    const charCodes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      charCodes[i] = binaryString.charCodeAt(i);
    }

    const decompressed = pako.inflate(charCodes, { to: 'string' });

    return JSON.parse(decompressed) as T;
  } catch (e) {
    return null;
  }
};

export const getHost = (): string => {
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  const port = window.location.port;

  const portSegment = port ? `:${port}` : '';

  return `${protocol}//${hostname}${portSegment}`;
};

export const extractPath = (fullUrl: string): string | null => {
  try {
    const url = new URL(fullUrl);
    return url.pathname + url.search;
  } catch (error) {
    return null;
  }
};

export const changeHostToCurrent = (url: string): string => {
  const host = getHost();

  const onlyPath = extractPath(url);

  if (!onlyPath) {
    return url;
  }

  return host + onlyPath;
};
